import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const API_BASE = 'https://collabbayadmin.com/APIControllers';

const AssignVouchers = ({ navigation }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [creators, setCreators] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const [Stock, setStock] = useState('');
  const [OnSpend, setOnSpend] = useState('');
  const [Discount, setDiscount] = useState('');

  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchCreators();
  }, []);

  const fetchCreators = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API_BASE}/FetchCreatorsForVouchers.php`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (data.success) {
        setBrandName(data.BrandName || '');
        setCreators(Array.isArray(data.creators) ? data.creators : []);
      } else {
        Alert.alert('Error', data.message || 'Could not fetch creators.');
      }
    } catch (error) {
      console.error('Fetch creators error:', error);
      Alert.alert('Error', 'Something went wrong fetching creators.');
    } finally {
      setLoading(false);
    }
  };

  const selectCreator = (creator) => {
    setSelectedCreator(creator);
    setSuccessMessage('');
  };

  const resetForm = () => {
    setSelectedCreator(null);
    setStock('');
    setOnSpend('');
    setDiscount('');
    setSuccessMessage('');
  };

  const submitVoucherStock = async () => {
    if (!selectedCreator) {
      Alert.alert('Missing creator', 'Please select a creator first.');
      return;
    }

    if (!Stock || !OnSpend || !Discount) {
      Alert.alert('Missing fields', 'Please enter Stock, OnSpend and Discount.');
      return;
    }

    if (isNaN(Number(Stock)) || isNaN(Number(OnSpend)) || isNaN(Number(Discount))) {
      Alert.alert('Invalid fields', 'Stock, OnSpend and Discount must be numbers.');
      return;
    }

    if (Number(Stock) <= 0 || Number(OnSpend) <= 0 || Number(Discount) <= 0) {
      Alert.alert('Invalid fields', 'Stock, OnSpend and Discount must be greater than 0.');
      return;
    }

    try {
      setSubmitting(true);
      setSuccessMessage('');

      const payload = {
        CollabID: selectedCreator.CollabID,
        Stock,
        OnSpend,
        Discount,
      };

      const response = await fetch(`${API_BASE}/InsertVoucherStock.php`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Voucher stock assigned successfully.');
        Alert.alert('Success', 'Voucher stock assigned successfully.');
        await fetchCreators();
      } else {
        Alert.alert('Error', data.message || 'Could not assign voucher stock.');
      }
    } catch (error) {
      console.error('Insert voucher stock error:', error);
      Alert.alert('Error', 'Something went wrong assigning voucher stock.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingPage}>
        <ActivityIndicator size="large" color="#000" />
        <Text style={styles.loadingText}>Loading eligible creators...</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.topBar}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="chevron-back" size={20} color="#111" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>

        <View style={styles.brandPill}>
          <Ionicons name="business-outline" size={15} color="#111" />
          <Text style={styles.brandPillText}>{brandName || 'Brand'}</Text>
        </View>
      </View>

      <View style={styles.heroCard}>
        <Text style={styles.eyebrow}>Voucher stock</Text>
        <Text style={styles.title}>Assign creator vouchers</Text>
        <Text style={styles.subtitle}>
          Select a creator from your delivered collaborations, then define the voucher offer and how many vouchers they can distribute.
        </Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>1. Select creator</Text>
        <Text style={styles.sectionSubtext}>
          These creators are pulled from your past collaborations where content exists in postuploads.
        </Text>

        {creators.length === 0 ? (
          <View style={styles.emptyBox}>
            <Ionicons name="alert-circle-outline" size={24} color="#777" />
            <Text style={styles.emptyTitle}>No eligible creators found</Text>
            <Text style={styles.emptyText}>
              Once a collaboration has uploaded content, it should appear here.
            </Text>
          </View>
        ) : (
          <View style={styles.creatorList}>
            {creators.map((creator) => {
              const isSelected = selectedCreator?.CollabID === creator.CollabID;

              return (
                <TouchableOpacity
                  key={creator.CollabID}
                  style={[
                    styles.creatorCard,
                    isSelected && styles.creatorCardSelected,
                  ]}
                  onPress={() => selectCreator(creator)}
                  activeOpacity={0.85}
                >
                  <View style={styles.creatorLeft}>
                    <View style={styles.avatarCircle}>
                      <Text style={styles.avatarText}>
                        {(creator.Promoter || '?').charAt(0).toUpperCase()}
                      </Text>
                    </View>

                    <View style={styles.creatorInfo}>
                      <Text style={styles.creatorName}>{creator.Promoter}</Text>
                      <Text style={styles.creatorMeta}>CollabID: {creator.CollabID}</Text>
                      <Text style={styles.creatorMeta}>BrandName: {creator.BrandName}</Text>
                    </View>
                  </View>

                  <View style={[
                    styles.selectIndicator,
                    isSelected && styles.selectIndicatorActive,
                  ]}>
                    {isSelected && <Ionicons name="checkmark" size={16} color="#fff" />}
                  </View>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      {selectedCreator && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>2. Define voucher</Text>
          <Text style={styles.sectionSubtext}>
            These fields create one row in voucherstock using the selected CollabID.
          </Text>

          <View style={styles.selectedBox}>
            <View style={styles.selectedRow}>
              <Text style={styles.selectedLabel}>CollabID</Text>
              <Text style={styles.selectedValue}>{selectedCreator.CollabID}</Text>
            </View>
            <View style={styles.selectedRow}>
              <Text style={styles.selectedLabel}>Promoter</Text>
              <Text style={styles.selectedValue}>{selectedCreator.Promoter}</Text>
            </View>
            <View style={styles.selectedRow}>
              <Text style={styles.selectedLabel}>BrandName</Text>
              <Text style={styles.selectedValue}>{selectedCreator.BrandName}</Text>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>OnSpend</Text>
            <Text style={styles.helpText}>
              How much the customer must spend to unlock the voucher discount.
            </Text>
            <TextInput
              style={styles.input}
              value={OnSpend}
              onChangeText={setOnSpend}
              placeholder="Example: 50"
              keyboardType="numeric"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Discount</Text>
            <Text style={styles.helpText}>
              The percentage discount the voucher buyer receives. Store as a plain number.
            </Text>
            <TextInput
              style={styles.input}
              value={Discount}
              onChangeText={setDiscount}
              placeholder="Example: 20"
              keyboardType="numeric"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.inputLabel}>Stock</Text>
            <Text style={styles.helpText}>
              How many vouchers this creator can distribute to their audience.
            </Text>
            <TextInput
              style={styles.input}
              value={Stock}
              onChangeText={setStock}
              placeholder="Example: 100"
              keyboardType="numeric"
              placeholderTextColor="#999"
            />
          </View>

          <View style={styles.previewCard}>
            <Text style={styles.previewTitle}>Voucher preview</Text>
            <Text style={styles.previewText}>
              {Discount || 'X'}% off when you spend £{OnSpend || 'Y'}
            </Text>
            <Text style={styles.previewSubtext}>
              {selectedCreator.Promoter} will receive {Stock || '0'} vouchers to distribute.
            </Text>
          </View>

          {successMessage ? (
            <View style={styles.successBox}>
              <Ionicons name="checkmark-circle" size={22} color="#0a7d28" />
              <Text style={styles.successText}>{successMessage}</Text>
            </View>
          ) : null}

          <View style={styles.buttonRow}>
            <TouchableOpacity
              style={styles.secondaryButton}
              onPress={resetForm}
              disabled={submitting}
            >
              <Text style={styles.secondaryButtonText}>Reset</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.primaryButton, submitting && styles.primaryButtonDisabled]}
              onPress={submitVoucherStock}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <>
                  <Ionicons name="add-circle-outline" size={19} color="#fff" />
                  <Text style={styles.primaryButtonText}>Assign vouchers</Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  pageContent: {
    padding: 18,
    paddingBottom: 60,
    width: '100%',
    maxWidth: 980,
    alignSelf: 'center',
  },
  loadingPage: {
    flex: 1,
    backgroundColor: '#fff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: '#666',
  },
  topBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 14,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e7e7e7',
  },
  backText: {
    marginLeft: 4,
    fontSize: 14,
    fontWeight: '600',
    color: '#111',
  },
  brandPill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#e7e7e7',
  },
  brandPillText: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '700',
    color: '#111',
  },
  heroCard: {
    backgroundColor: '#111',
    borderRadius: 28,
    padding: 24,
    marginBottom: 18,
  },
  eyebrow: {
    color: '#FDF05D',
    fontSize: 13,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  title: {
    color: '#fff',
    fontSize: 30,
    fontWeight: '900',
    marginBottom: 10,
  },
  subtitle: {
    color: '#d8d8d8',
    fontSize: 15,
    lineHeight: 22,
    maxWidth: 680,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 18,
    marginBottom: 18,
    borderWidth: 1,
    borderColor: '#e8e8e8',
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '900',
    color: '#111',
    marginBottom: 6,
  },
  sectionSubtext: {
    fontSize: 14,
    lineHeight: 20,
    color: '#666',
    marginBottom: 16,
  },
  creatorList: {
    gap: 10,
  },
  creatorCard: {
    borderWidth: 1,
    borderColor: '#ebebeb',
    borderRadius: 18,
    padding: 14,
    backgroundColor: '#fafafa',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  creatorCardSelected: {
    borderColor: '#111',
    backgroundColor: '#f2f2f2',
  },
  creatorLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatarCircle: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: '#111',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  avatarText: {
    color: '#fff',
    fontWeight: '900',
    fontSize: 18,
  },
  creatorInfo: {
    flex: 1,
  },
  creatorName: {
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
    marginBottom: 3,
  },
  creatorMeta: {
    fontSize: 12,
    color: '#666',
    marginTop: 2,
  },
  selectIndicator: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#bbb',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 12,
  },
  selectIndicatorActive: {
    backgroundColor: '#111',
    borderColor: '#111',
  },
  emptyBox: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 26,
    borderRadius: 18,
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderColor: '#ededed',
  },
  emptyTitle: {
    marginTop: 8,
    fontSize: 16,
    fontWeight: '900',
    color: '#111',
  },
  emptyText: {
    marginTop: 4,
    fontSize: 13,
    color: '#777',
    textAlign: 'center',
  },
  selectedBox: {
    backgroundColor: '#f7f7f7',
    borderRadius: 18,
    padding: 14,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#ededed',
  },
  selectedRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 7,
    borderBottomWidth: 1,
    borderBottomColor: '#e9e9e9',
  },
  selectedLabel: {
    fontSize: 13,
    fontWeight: '800',
    color: '#555',
  },
  selectedValue: {
    fontSize: 13,
    fontWeight: '900',
    color: '#111',
    flex: 1,
    textAlign: 'right',
  },
  inputGroup: {
    marginBottom: 14,
  },
  inputLabel: {
    fontSize: 15,
    fontWeight: '900',
    color: '#111',
    marginBottom: 4,
  },
  helpText: {
    fontSize: 12,
    color: '#777',
    lineHeight: 17,
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#dcdcdc',
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 13,
    fontSize: 15,
    color: '#111',
    outlineStyle: 'none',
  },
  previewCard: {
    backgroundColor: '#111',
    borderRadius: 20,
    padding: 18,
    marginTop: 6,
    marginBottom: 16,
  },
  previewTitle: {
    color: '#FDF05D',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  previewText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '900',
    marginBottom: 6,
  },
  previewSubtext: {
    color: '#d7d7d7',
    fontSize: 13,
    lineHeight: 19,
  },
  successBox: {
    backgroundColor: '#eaf8ee',
    borderWidth: 1,
    borderColor: '#bfe7ca',
    borderRadius: 16,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
  },
  successText: {
    marginLeft: 8,
    color: '#0a7d28',
    fontWeight: '800',
    fontSize: 14,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    flexWrap: 'wrap',
  },
  secondaryButton: {
    paddingVertical: 13,
    paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: '#f1f1f1',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  secondaryButtonText: {
    color: '#111',
    fontWeight: '900',
    fontSize: 14,
  },
  primaryButton: {
    paddingVertical: 13,
    paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: '#111',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 170,
  },
  primaryButtonDisabled: {
    opacity: 0.65,
  },
  primaryButtonText: {
    color: '#fff',
    fontWeight: '900',
    fontSize: 14,
    marginLeft: 7,
  },
});

export default AssignVouchers;